import { Box, Flex } from '@chakra-ui/react';

import { TextBold, TextMedium, TextRegular } from 'UiKit/typography';
import ButtonOutline from 'UiKit/button/buttonOutline';
import { AlertOutlineIcon, ForkSpoonIcon, HandShakeIcon, PrayIcon, ToiletIcon } from 'Asset/icon';
import ButtonSolid from 'UiKit/button/buttonSolid';
import useGetBreak from 'Domain/monitoring/useGetBreak';
import Tooltip from 'UiKit/tooltip';
import { Condition } from 'Component/others';
import useCaptchaSchedule from 'Store/captchaSchedule';
import { breakTooltip } from 'Const/monitoring/view';

import useOpenBreakConfirmation from 'View/monitoring/hook/useOpenBreakConfirmation';
import useOpenClockOutConfirmation from 'View/monitoring/hook/useOpenClockOutConfirmation';
import ClockInSkeleton from 'View/monitoring/component/skeleton/clock-in-skeleton';

import styles from './styles.module.css';

const Clockin = ({ monitoringStatus, isFetching, t }) => {
  const onOpenBreakConfirmationModal = useOpenBreakConfirmation();
  const onOpenClockOutConfirmationModal = useOpenClockOutConfirmation();
  const { triggerClear } = useCaptchaSchedule();

  const { data: breaks } = useGetBreak(monitoringStatus);

  const onClickBreakDetail = (breakDetail) => {
    onOpenBreakConfirmationModal({ breakDetail, monitoringStatus, customOnSubmitAction: () => triggerClear() });
  };
  const onClickClockOut = () => onOpenClockOutConfirmationModal({ monitoringStatus });

  return (
    <Condition statement={!isFetching} fallback={<ClockInSkeleton />}>
      <Box className={styles['clockin__container']}>
        <Box className={styles['clockin__section--head']}>
          <Box>
            <TextBold className={styles['shift__text']}>{monitoringStatus?.shift?.name}</TextBold>
            <TextRegular className={styles['time__text']}>{monitoringStatus?.shift?.dateTime}</TextRegular>
          </Box>

          <ButtonOutline data-testid="clockout-button" className={styles['clock-out__button']} onClick={onClickClockOut}>
            {t('clock_in_view.clockOutText')}
          </ButtonOutline>
        </Box>
        <Box className={styles['clockin__section--content']}>
          {/* Makan */}

          <Box className={styles['clockin-item__card']}>
            <Flex className={styles['card__section--head']}>
              <TextBold className={styles['clockin-variant__text']}>{t('clock_in_view.mealBreakText')}</TextBold>
              <Tooltip
                align="left"
                label={breakTooltip.mealBreakTooltipText({ t })}
                children={<AlertOutlineIcon size="14px" color="var(--color-primary40)" />}
              />
            </Flex>

            <Flex className={styles['card__section--content']}>
              <Flex gap="8px" alignItems="center">
                <ForkSpoonIcon color="var(--color-neutral50)" />
                <TextMedium className={styles['clockin-usage__text']}>
                  {breaks?.mealBreak?.used} <span>/ {breaks?.mealBreak?.total}</span>
                </TextMedium>
              </Flex>

              <TextMedium className={styles['clockin-duration__text']}>
                {t('clock_in_view.remainingText')}
                <span>{breaks?.mealBreak?.remainingDuration}</span>
                <span>{t('clock_in_view.minuteText')}</span>
              </TextMedium>
            </Flex>

            <ButtonSolid
              isDisabled={breaks?.mealBreak?.isDisabled}
              data-testid="meal-button"
              className={styles['clockin__button']}
              onClick={() => onClickBreakDetail(breaks?.mealBreak)}
            >
              {t('clock_in_view.mealText')}
            </ButtonSolid>
          </Box>

          {/* Sholat */}

          <Box className={styles['clockin-item__card']}>
            <Flex className={styles['card__section--head']}>
              <TextBold className={styles['clockin-variant__text']}>{t('clock_in_view.prayBreakText')}</TextBold>
              <Tooltip
                align="left"
                label={t('clock_in_view.prayBreakTooltipText')}
                children={<AlertOutlineIcon size="14px" color="var(--color-primary40)" />}
              />
            </Flex>

            <Flex className={styles['card__section--content']}>
              <Flex gap="8px" alignItems="center">
                <PrayIcon color="var(--color-neutral50)" />
                <TextMedium className={styles['clockin-usage__text']}>
                  {breaks?.prayBreak?.used} <span>/ {breaks?.prayBreak?.total}</span>
                </TextMedium>
              </Flex>

              <TextMedium className={styles['clockin-duration__text']}>
                {breaks?.prayBreak?.totalDuration} {t('clock_in_view.hourPerSessionText')}
              </TextMedium>
            </Flex>

            <ButtonSolid
              isDisabled={breaks?.prayBreak?.isDisabled}
              data-testid="toilet-button"
              className={styles['clockin__button']}
              onClick={() => onClickBreakDetail(breaks?.prayBreak)}
            >
              {t('clock_in_view.prayText')}
            </ButtonSolid>
          </Box>

          {/* Toilet */}

          <Box className={styles['clockin-item__card']}>
            <Flex className={styles['card__section--head']}>
              <TextBold className={styles['clockin-variant__text']}>{t('clock_in_view.toiletBreakText')}</TextBold>
              <Tooltip
                align="left"
                htmlComponent={t('clock_in_view.toiletBreakTooltipText')}
                children={<AlertOutlineIcon size="14px" color="var(--color-primary40)" />}
              />
            </Flex>

            <Flex className={styles['card__section--content']}>
              <Flex gap="8px" alignItems="center">
                <ToiletIcon color="var(--color-neutral50)" />
                <TextMedium className={styles['clockin-usage__text']}>
                  {breaks?.toiletBreak?.used} <span>/ {breaks?.toiletBreak?.total}</span>
                </TextMedium>
              </Flex>

              <TextMedium className={styles['clockin-duration__text']}>
                {t('clock_in_view.remainingText')}
                <span>{breaks?.toiletBreak?.remainingDuration}</span>
                <span>{t('clock_in_view.minuteText')}</span>
              </TextMedium>
            </Flex>

            <ButtonSolid
              isDisabled={breaks?.toiletBreak?.isDisabled}
              data-testid="pray-button"
              className={styles['clockin__button']}
              onClick={() => onClickBreakDetail(breaks?.toiletBreak)}
            >
              {t('clock_in_view.toiletText')}
            </ButtonSolid>
          </Box>

          {/* Bantu Rekan */}

          <Box className={styles['clockin-item__card']}>
            <Flex className={styles['card__section--head']}>
              <TextBold className={styles['clockin-variant__text']}>{t('clock_in_view.helpPartnerText')}</TextBold>
              <Tooltip
                align="left"
                label={
                  <Flex flexDirection={'column'} gap="8px" textAlign={'left'}>
                    <TextRegular>{t('clock_in_view.helpPartnerBreakTooltipText')}</TextRegular>
                    <TextRegular>{t('clock_in_view.helpPartnerBreakThresholdTooltipText')}</TextRegular>
                  </Flex>
                }
                children={<AlertOutlineIcon size="14px" color="var(--color-primary40)" />}
                width={260}
                placement="right"
              />
            </Flex>

            <Flex className={styles['card__section--content']}>
              <Flex gap="8px" alignItems="center">
                <HandShakeIcon color="var(--color-neutral50)" />
                <TextMedium className={styles['clockin-usage__text']}>{breaks?.helpPartner?.used}</TextMedium>
              </Flex>
            </Flex>

            <ButtonSolid data-testid="help-partner-button" className={styles['clockin__button']} onClick={() => onClickBreakDetail(breaks?.helpPartner)}>
              {t('clock_in_view.helpPartnerText')}
            </ButtonSolid>
          </Box>
        </Box>
      </Box>
    </Condition>
  );
};

export default Clockin;
